/**
 * Read VIN barcodes from a canvas.
 *
 * The original scanner used the JavaScript ZXing port (@zxing/browser). That
 * port is weak on wide Code 39 (the format on US door-jamb labels) and did
 * not even search PDF417, which is what most 2010+ certification labels
 * actually print. It also treated decodeFromCanvas as a synchronous call and
 * upscaled with nearest-neighbour, both of which silently produced "nothing
 * in this frame."
 *
 * This path uses the C++ ZXing WebAssembly build, which already tries
 * rotation, inversion and downscaling, plus the native BarcodeDetector API
 * where the browser has one (Chrome on Android is excellent).
 */

import type { VinRead } from "./parse";
import { readVin } from "./parse";

const VIN_FORMATS = [
  "Code39",
  "Code39Std",
  "Code128",
  "Code93",
  "PDF417",
  "DataMatrix",
  "QRCode",
] as const;

const NATIVE_FORMATS = [
  "code_39",
  "code_128",
  "pdf417",
  "data_matrix",
  "qr_code",
] as const;

type NativeDetector = {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>;
};

let nativeDetector: NativeDetector | null | undefined;
let wasmReady: Promise<boolean> | null = null;

function getNativeDetector(): NativeDetector | null {
  if (nativeDetector !== undefined) return nativeDetector;
  nativeDetector = null;
  if (typeof window === "undefined") return null;
  const Ctor = (window as unknown as {
    BarcodeDetector?: new (opts: { formats: string[] }) => NativeDetector;
  }).BarcodeDetector;
  if (!Ctor) return null;
  try {
    nativeDetector = new Ctor({ formats: [...NATIVE_FORMATS] });
  } catch {
    try {
      nativeDetector = new Ctor({ formats: ["code_39", "code_128", "qr_code"] });
    } catch {
      nativeDetector = null;
    }
  }
  return nativeDetector;
}

async function ensureWasm(): Promise<boolean> {
  if (wasmReady) return wasmReady;
  wasmReady = (async () => {
    try {
      const { prepareZXingModule } = await import("zxing-wasm/reader");
      if (typeof window !== "undefined") {
        await prepareZXingModule({
          fireImmediately: true,
          overrides: {
            locateFile: (path: string, prefix: string) =>
              path.endsWith(".wasm") ? "/scan/zxing_reader.wasm" : prefix + path,
          },
        });
      } else {
        await prepareZXingModule({ fireImmediately: true });
      }
      return true;
    } catch {
      wasmReady = null;
      return false;
    }
  })();
  return wasmReady;
}

export function warmupBarcodeEngine(): Promise<boolean> {
  return ensureWasm();
}

async function decodeNative(canvas: HTMLCanvasElement): Promise<string[]> {
  const det = getNativeDetector();
  if (!det) return [];
  try {
    const hits = await det.detect(canvas);
    return hits.map((h) => h.rawValue).filter(Boolean);
  } catch {
    return [];
  }
}

async function decodeWasm(canvas: HTMLCanvasElement): Promise<string[]> {
  const ok = await ensureWasm();
  if (!ok) return [];
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return [];
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  try {
    const { readBarcodes } = await import("zxing-wasm/reader");
    const results = await readBarcodes(imageData, {
      formats: [...VIN_FORMATS],
      tryHarder: true,
      tryRotate: true,
      tryInvert: true,
      tryDownscale: true,
      maxNumberOfSymbols: 8,
      textMode: "Plain",
    });
    return results
      .filter((r) => r.isValid && r.text)
      .map((r) => r.text);
  } catch {
    return [];
  }
}

function invertCanvas(source: HTMLCanvasElement, target: HTMLCanvasElement): boolean {
  target.width = source.width;
  target.height = source.height;
  const ctx = target.getContext("2d", { willReadFrequently: true });
  const src = source.getContext("2d", { willReadFrequently: true });
  if (!ctx || !src) return false;
  ctx.drawImage(source, 0, 0);
  const img = ctx.getImageData(0, 0, target.width, target.height);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 255 - d[i]!;
    d[i + 1] = 255 - d[i + 1]!;
    d[i + 2] = 255 - d[i + 2]!;
  }
  ctx.putImageData(img, 0, 0);
  return true;
}

/**
 * Run every engine we have against a canvas. Returns the first payload that
 * contains a VIN, or the raw payloads if none did (so the UI can show them).
 */
export async function decodeBarcodeCanvas(
  canvas: HTMLCanvasElement,
): Promise<{ read: VinRead | null; payloads: string[] }> {
  const payloads: string[] = [];
  const seen = new Set<string>();
  const add = (list: string[]) => {
    for (const t of list) {
      const v = t.trim();
      if (v && !seen.has(v)) {
        seen.add(v);
        payloads.push(v);
      }
    }
  };

  add(await decodeNative(canvas));
  add(await decodeWasm(canvas));

  for (const text of payloads) {
    const read = readVin(text);
    if (read) return { read, payloads };
  }

  // Native/wasm already invert internally, but a hard photometric invert
  // still helps on silver/black door stickers the binarizer guessed wrong.
  if (payloads.length === 0) {
    const inv = document.createElement("canvas");
    if (invertCanvas(canvas, inv)) {
      add(await decodeWasm(inv));
      for (const text of payloads) {
        const read = readVin(text);
        if (read) return { read, payloads };
      }
    }
  }

  return { read: null, payloads };
}
